import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import snipeLogo from '../../assets/snipe_logo.png';
import { useAuth } from '../../context/AuthContext';

// Tooltip rendered to the right of an icon when the sidebar is collapsed.
// Pure-CSS group-hover reveal — no JS, no flicker. The active item gets a
// subtle emerald accent so it stays visually distinct on hover.
function CollapsedTooltip({ label, isActive }) {
    return (
        <span
            role="tooltip"
            className={`pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 z-[10000]
                whitespace-nowrap px-2.5 py-1 rounded-md text-[11px] font-medium tracking-tight
                bg-slate-800/95 backdrop-blur border shadow-xl shadow-black/40
                opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0
                transition-all duration-150 ${
                    isActive
                        ? 'border-emerald-500/30 text-emerald-300'
                        : 'border-slate-700/60 text-slate-200'
                }`}
        >
            {/* Arrow / pointer */}
            <span
                aria-hidden="true"
                className={`absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 w-0 h-0
                    border-y-[5px] border-y-transparent border-r-[5px] ${
                        isActive ? 'border-r-emerald-500/30' : 'border-r-slate-700/60'
                    }`}
            />
            <span
                aria-hidden="true"
                className={`absolute left-0 top-1/2 -translate-x-[calc(100%-1px)] -translate-y-1/2 w-0 h-0
                    border-y-[5px] border-y-transparent border-r-[5px] border-r-slate-800/95`}
            />
            {label}
        </span>
    );
}

// Account control pinned to the sidebar footer. Opens an upward popover with the
// signed-in email + sign out. Works in both collapsed and expanded states.
function SideNavAccount({ collapsed }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const avatarLetter = (user?.email?.charAt(0) || 'A').toUpperCase();
    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <div className="border-t border-slate-800/60 px-2 py-2 relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label="Account"
                className={`w-full flex items-center rounded-md transition-colors duration-150 hover:bg-slate-800/40 ${
                    collapsed ? 'justify-center px-1 py-2' : 'gap-2.5 px-2 py-2'
                } ${open ? 'bg-slate-800/40' : ''}`}
            >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                    {avatarLetter}
                </div>
                {!collapsed && (
                    <>
                        <div className="flex-1 min-w-0 text-left">
                            <div className="text-[11px] font-medium text-slate-200 truncate">{user?.email || 'Account'}</div>
                            <div className="text-[10px] text-slate-500 leading-none">View account</div>
                        </div>
                        <svg
                            className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-200 flex-shrink-0 ${open ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                        </svg>
                    </>
                )}
            </button>

            {open && (
                <div className="absolute bottom-full mb-2 left-2 w-60 bg-[#0a1224] border border-slate-800 rounded-lg shadow-xl shadow-black/50 overflow-hidden z-[10001] animate-sideNavPop">
                    <div className="px-3 py-2.5 border-b border-slate-800/80">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Signed in</p>
                        <p className="text-sm text-slate-200 truncate">{user?.email || 'operator@unknown'}</p>
                    </div>
                    <div className="py-1">
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-slate-400 hover:bg-slate-800/60 hover:text-red-400 transition-colors duration-150"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            <span className="text-sm font-medium">Sign out</span>
                        </button>
                    </div>
                </div>
            )}
            <style jsx>{`
                @keyframes sideNavPop {
                    from { opacity: 0; transform: translateY(4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-sideNavPop { animation: sideNavPop 0.16s ease-out forwards; }
            `}</style>
        </div>
    );
}

function SideNav({ activeTab, setActiveTab, collapsed = false, onToggleCollapsed }) {
    const { user, hasRole } = useAuth();
    const discordLinked = user?.discordLinked;
    const isAdmin = hasRole('ADMIN');
    const canUseSnipeHub = true;
    const canUsePropTool = true;

    const discordItem = {
        id: 'discord',
        label: 'Discord',
        icon: 'M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z',
        showWhen: true,
    };
    const snipeHubItem = {
        id: 'snipe-hub',
        label: 'Snipe Hub',
        icon: 'M3.75 6A2.25 2.25 0 016 3.75h3A2.25 2.25 0 0111.25 6v3A2.25 2.25 0 019 11.25H6A2.25 2.25 0 013.75 9V6zm9 0A2.25 2.25 0 0115 3.75h3A2.25 2.25 0 0120.25 6v3A2.25 2.25 0 0118 11.25h-3A2.25 2.25 0 0112.75 9V6zm-9 9A2.25 2.25 0 016 12.75h3A2.25 2.25 0 0111.25 15v3A2.25 2.25 0 019 20.25H6A2.25 2.25 0 013.75 18v-3zm9 0A2.25 2.25 0 0115 12.75h3A2.25 2.25 0 0120.25 15v3A2.25 2.25 0 0118 20.25h-3A2.25 2.25 0 0112.75 18v-3z',
        showWhen: canUseSnipeHub,
    };
    const propToolItem = {
        id: 'prop-tool',
        label: 'Prop Tool',
        icon: 'M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z',
        showWhen: canUsePropTool,
    };
    const snipeFeedItem = {
        id: 'snipe-feed',
        label: 'Snipe Feed',
        icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
        showWhen: true,
    };
    const oddsScreenItem = {
        id: 'odds-screen',
        label: 'Odds Screen',
        icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
        showWhen: true,
    };
    const hotPropsItem = {
        id: 'hot-props',
        label: 'Hot Props',
        icon: 'M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003.436 4.314 9.018 9.018 0 01-.436-1.833zM12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.975 5.975 0 01-2.133-1.001A3.75 3.75 0 0012 18z',
        showWhen: true,
        accent: 'amber',
    };
    const multiBuilderItem = {
        id: 'multi-builder',
        label: 'Multi Builder',
        icon: 'M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3',
        showWhen: true,
    };
    const resultsTrackerItem = {
        id: 'results-tracker',
        label: 'Results',
        icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
        showWhen: true,
    };
    const manageSubsItem = {
        id: 'manage-subscriptions',
        label: 'Billing',
        icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
        showWhen: true,
    };
    const adminToolsItem = {
        id: 'admin-tools',
        label: 'Admin',
        icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
        showWhen: isAdmin,
    };

    const allMenuItems = discordLinked
        ? [snipeHubItem, propToolItem, snipeFeedItem, oddsScreenItem, hotPropsItem, multiBuilderItem, resultsTrackerItem, adminToolsItem, manageSubsItem, discordItem]
        : [snipeHubItem, propToolItem, discordItem, snipeFeedItem, oddsScreenItem, hotPropsItem, multiBuilderItem, resultsTrackerItem, adminToolsItem, manageSubsItem];

    const menuItems = allMenuItems.filter(item => item.showWhen);

    const handleNavClick = (itemId) => {
        setActiveTab(itemId);
    };

    return (
        <aside
            className={`fixed left-0 top-0 h-full bg-[#060b18] border-r border-slate-800/60 hidden md:flex flex-col
                transition-[width] duration-200 ease-out ${
                    collapsed ? 'w-[72px]' : 'w-[104px]'
                }`}
            style={{ zIndex: 9999 }}
            aria-label="Primary"
            data-collapsed={collapsed ? 'true' : 'false'}
        >
            {/* Logo slot — fixed 72px tall so it becomes a perfect square when collapsed */}
            <div className="h-[72px] flex items-center justify-center border-b border-slate-800/60">
                <img src={snipeLogo} alt="Snipe" className="h-9 w-auto object-contain" />
            </div>

            {/* Nav */}
            <nav className="flex-1 py-4 px-2">
                <ul className="space-y-1">
                    {menuItems.map((item) => {
                        const isActive = activeTab === item.id;
                        const isFilled = item.accent === 'amber';
                        return (
                            <li key={item.id} className="relative group">
                                {/* Active left rail */}
                                <span
                                    aria-hidden="true"
                                    className={`absolute -left-2 top-2 bottom-2 w-[2px] rounded-r transition-all duration-200 ${
                                        isActive ? 'bg-emerald-400' : 'bg-transparent'
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => handleNavClick(item.id)}
                                    title={collapsed ? undefined : item.label}
                                    aria-label={item.label}
                                    aria-current={isActive ? 'page' : undefined}
                                    className={`w-full flex flex-col items-center gap-1.5 rounded-md transition-colors duration-150 ${
                                        collapsed ? 'px-1 py-2.5' : 'px-1 py-2.5'
                                    } ${
                                        isActive
                                            ? 'text-white bg-slate-800/40'
                                            : 'text-slate-500 hover:text-white hover:bg-slate-800/30'
                                    }`}
                                >
                                    <svg
                                        className={`w-5 h-5 transition-colors ${
                                            isActive ? 'text-emerald-400' : 'text-current'
                                        }`}
                                        fill={isFilled ? 'currentColor' : 'none'}
                                        stroke={isFilled ? 'none' : 'currentColor'}
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={isFilled ? '0' : '1.75'}
                                            d={item.icon}
                                        />
                                    </svg>
                                    {/* Label — animates open/closed via opacity + max-height so the
                                        collapsing motion feels continuous, not abrupt. */}
                                    <span
                                        className={`text-[10px] font-medium tracking-wide leading-none overflow-hidden transition-all duration-150 ${
                                            collapsed
                                                ? 'opacity-0 max-h-0 mt-0'
                                                : 'opacity-100 max-h-4'
                                        }`}
                                    >
                                        {item.label}
                                    </span>
                                </button>

                                {/* Hover tooltip — only when collapsed */}
                                {collapsed && (
                                    <CollapsedTooltip label={item.label} isActive={isActive} />
                                )}
                            </li>
                        );
                    })}
                </ul>
            </nav>

            {/* Account */}
            <SideNavAccount collapsed={collapsed} />

            {/* Collapse / expand toggle */}
            {typeof onToggleCollapsed === 'function' && (
                <div className="border-t border-slate-800/60 px-2 py-2 relative group">
                    <button
                        type="button"
                        onClick={onToggleCollapsed}
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        aria-pressed={collapsed}
                        className="w-full flex items-center justify-center px-2 py-2 rounded-md text-slate-500 hover:text-white hover:bg-slate-800/40 transition-colors duration-150"
                    >
                        <svg
                            className={`w-4 h-4 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                    {collapsed && (
                        <CollapsedTooltip label="Expand" isActive={false} />
                    )}
                </div>
            )}
        </aside>
    );
}

export default SideNav;
